// Copyright 2025, University of Maryland, All Rights Reserved
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Eye, CheckCircle, AlertTriangle } from "lucide-react"
import type { JSONSchema } from "@/api/types/schema"
import type { MonitorAnalysis } from "@/types/workspace-bill"
import { formatSchemaForDisplay } from "@/utils/schema/schema-utils"
import { BillSections } from "@/components/BillSections"

interface SchemaPreviewProps {
  schema: JSONSchema | null
  validation: { valid: boolean; errors: string[] }
}

// Generate sample analysis data based on schema structure
function generateSampleAnalysis(schema: JSONSchema): MonitorAnalysis {
  const sample_analysis: MonitorAnalysis = {
    title: String(schema.title || 'Analysis Results')
  }

  // Sample data for different field types
  const sample_integers = [8, 3, 5, 7, 2, 6, 9, 4, 1, 10]
  const sample_texts = [
    "This bill would have a significant positive impact on the organization's operations by streamlining processes and reducing administrative burden.",
    "The proposed changes could create compliance challenges that may require additional resources and training for staff.",
    "While the impact is moderate, the bill addresses important regulatory gaps that could affect long-term sustainability.",
    "This legislation aligns well with current strategic priorities and could enhance operational efficiency.",
    "The financial implications of this bill may strain current budget allocations and require careful planning.",
    "This bill provides a balanced approach that addresses key concerns while maintaining operational flexibility."
  ]
  const sample_enums = ['positive', 'negative', 'neutral', 'high', 'medium', 'low', 'critical', 'moderate', 'minimal']
  const sample_scores = [
    { score: 8, score_type: 'positive' },
    { score: 3, score_type: 'negative' },
    { score: 5, score_type: 'neutral' },
    { score: 7, score_type: 'positive' },
    { score: 2, score_type: 'negative' },
    { score: 6, score_type: 'neutral' },
    { score: 9, score_type: 'positive' },
    { score: 4, score_type: 'neutral' },
    { score: 1, score_type: 'negative' },
    { score: 10, score_type: 'positive' }
  ]
  const sample_text_lists = [
    ['Increased funding for research programs', 'Enhanced student support services', 'Improved infrastructure development'],
    ['Compliance training requirements', 'Updated reporting procedures', 'Staff certification mandates'],
    ['Budget allocation changes', 'Resource redistribution', 'Cost-saving measures'],
    ['Stakeholder engagement', 'Community outreach', 'Public consultation process']
  ]
  const sample_object_lists = [
    [
      { name: 'Budget Impact', severity: 'high', timeline: '6 months' },
      { name: 'Staffing Changes', severity: 'medium', timeline: '3 months' },
      { name: 'Policy Updates', severity: 'low', timeline: '1 month' }
    ],
    [
      { department: 'Finance', impact_score: 8, action_required: 'immediate' },
      { department: 'HR', impact_score: 5, action_required: 'planned' },
      { department: 'Operations', impact_score: 3, action_required: 'monitoring' }
    ],
    [
      { stakeholder: 'Students', priority: 'high', engagement_level: 'active' },
      { stakeholder: 'Faculty', priority: 'medium', engagement_level: 'moderate' },
      { stakeholder: 'Administration', priority: 'high', engagement_level: 'active' }
    ]
  ]

  let integer_index = 0
  let text_index = 0
  let enum_index = 0
  let text_list_index = 0
  let object_list_index = 0
  let score_index = 0

  // Process each section defined in the schema
  for (const [section_id, section_schema] of Object.entries(schema.properties)) {
    if (section_id === 'title' || !section_schema.properties) continue

    const section_data: any = {}

    // Add icon if specified
    if (section_schema.properties.icon?.const) {
      section_data.icon = section_schema.properties.icon.const
    }

    // Add order if specified
    if (section_schema.properties.order?.const) {
      section_data.order = section_schema.properties.order.const
    }

    // Generate sample data for each field based on its type
    for (const [prop_key, prop_schema] of Object.entries(section_schema.properties)) {
      if (prop_key === 'title' || prop_key === 'icon' || prop_key === 'order') continue

      if (prop_schema.type === 'integer') {
        section_data[prop_key] = sample_integers[integer_index % sample_integers.length]
        integer_index++
      } else if (prop_schema.type === 'string' && prop_schema.enum) {
        // Enum field
        const enum_options = prop_schema.enum as string[]
        section_data[prop_key] = enum_options.length > 0 
          ? enum_options[enum_index % enum_options.length]
          : sample_enums[enum_index % sample_enums.length]
        enum_index++
      } else if (prop_schema.type === 'string') {
        // Text field
        section_data[prop_key] = sample_texts[text_index % sample_texts.length]
        text_index++
      } else if (prop_schema.type === 'array' && prop_schema.items?.type === 'string') {
        // Text list field
        section_data[prop_key] = sample_text_lists[text_list_index % sample_text_lists.length]
        text_list_index++
      } else if (prop_schema.type === 'array' && prop_schema.items?.type === 'object') {
        // Object list field - need to generate dynamic sample data based on the schema
        const items_properties = prop_schema.items.properties
        if (items_properties) {
          // Generate sample objects based on the actual schema properties
          const sample_objects = []
          for (let i = 0; i < 3; i++) {
            const sample_obj: any = {}
            for (const [obj_prop_key, obj_prop_schema] of Object.entries(items_properties)) {
              if (obj_prop_schema.type === 'string' && obj_prop_schema.enum) {
                // Enum property
                const enum_options = obj_prop_schema.enum as string[]
                sample_obj[obj_prop_key] = enum_options.length > 0 
                  ? enum_options[enum_index % enum_options.length]
                  : sample_enums[enum_index % sample_enums.length]
                enum_index++
              } else if (obj_prop_schema.type === 'string') {
                // String property
                sample_obj[obj_prop_key] = sample_texts[text_index % sample_texts.length]
                text_index++
              } else if (obj_prop_schema.type === 'integer') {
                // Integer property
                sample_obj[obj_prop_key] = sample_integers[integer_index % sample_integers.length]
                integer_index++
              } else if (obj_prop_schema.type === 'object' && obj_prop_schema.properties) {
                // Score object property
                const score_obj: any = {}
                for (const [score_prop_key, score_prop_schema] of Object.entries(obj_prop_schema.properties)) {
                  if (score_prop_key === 'score' && score_prop_schema.type === 'integer') {
                    score_obj[score_prop_key] = sample_scores[score_index % sample_scores.length].score
                  } else if (score_prop_key === 'score_type' && score_prop_schema.type === 'string' && score_prop_schema.enum) {
                    const enum_options = score_prop_schema.enum as string[]
                    score_obj[score_prop_key] = enum_options.length > 0 
                      ? enum_options[enum_index % enum_options.length]
                      : sample_scores[score_index % sample_scores.length].score_type
                  }
                }
                sample_obj[obj_prop_key] = score_obj
                score_index++
              }
            }
            sample_objects.push(sample_obj)
          }
          section_data[prop_key] = sample_objects
        } else {
          // Fallback to static sample data
          section_data[prop_key] = sample_object_lists[object_list_index % sample_object_lists.length]
          object_list_index++
        }
      } else if (prop_schema.type === 'object' && prop_schema.properties) {
        // Object field with sub-properties (score, etc.)
        const obj_data: any = {}
        
        // Generate sample data for each sub-property
        for (const [sub_key, sub_schema] of Object.entries(prop_schema.properties)) {
          if (sub_key === 'score' && sub_schema.type === 'integer') {
            // Score field - use score from sample_scores
            obj_data[sub_key] = sample_scores[score_index % sample_scores.length].score
          } else if (sub_key === 'score_type' && sub_schema.type === 'string' && sub_schema.enum) {
            // Score type field - use score_type from sample_scores or enum options
            const enum_options = sub_schema.enum as string[]
            obj_data[sub_key] = enum_options.length > 0 
              ? enum_options[enum_index % enum_options.length]
              : sample_scores[score_index % sample_scores.length].score_type
            enum_index++
          } else if (sub_schema.type === 'integer') {
            obj_data[sub_key] = sample_integers[integer_index % sample_integers.length]
            integer_index++
          } else if (sub_schema.type === 'string' && sub_schema.enum) {
            // Enum sub-field
            const enum_options = sub_schema.enum as string[]
            obj_data[sub_key] = enum_options.length > 0 
              ? enum_options[enum_index % enum_options.length]
              : sample_enums[enum_index % sample_enums.length]
            enum_index++
          } else if (sub_schema.type === 'string') {
            // Text sub-field
            obj_data[sub_key] = sample_texts[text_index % sample_texts.length]
            text_index++
          }
        }
        
        // Increment score_index after processing a complete score object
        if (prop_schema.properties.score && prop_schema.properties.score_type) {
          score_index++
        }
        
        section_data[prop_key] = obj_data
      }
    }

    sample_analysis[section_id] = section_data
  }

  return sample_analysis
}

export function SchemaPreview({ schema, validation }: SchemaPreviewProps): JSX.Element {
  if (!schema) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Eye className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>No schema to preview</p>
        <p className="text-sm">Configure sections in the builder to see the generated schema</p>
      </div>
    )
  }

  const formatted_schema = formatSchemaForDisplay(schema)
  const sample_analysis = generateSampleAnalysis(schema)


  return (
    <div className="space-y-6">
      {/* Validation Status */}
      <Alert variant={validation.valid ? "default" : "destructive"}>
        {validation.valid ? (
          <CheckCircle className="h-4 w-4" />
        ) : (
          <AlertTriangle className="h-4 w-4" />
        )}
        <AlertDescription>
          {validation.valid ? (
            <div className="flex items-center gap-2">
              <span className="font-medium">Schema is valid</span>
              <Badge variant="outline" className="text-xs">
                Ready to use
              </Badge>
            </div>
          ) : (
            <div className="space-y-1">
              <div className="font-medium">Schema validation failed:</div>
              <ul className="list-disc list-inside text-sm space-y-1">
                {validation.errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}
        </AlertDescription>
      </Alert>

      {/* Analysis Rendering Preview */}
      {validation.valid && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Analysis Preview
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              This is how the analysis will appear when rendered with your schema configuration
            </p>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg p-4 bg-gray-50 relative overflow-visible">
              <BillSections report_data={sample_analysis} schema={schema} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Raw Schema */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Raw JSON Schema</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <pre className="text-xs font-mono bg-gray-50 p-4 rounded-lg overflow-x-auto max-h-96">
              {formatted_schema}
            </pre>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}